import {all, call, delay, fork, put, select, takeEvery} from 'redux-saga/effects';
import {apiClient} from '../../../shared/api';
import {HttpStatus} from '../../../shared/constants/httpStatusCode';
import {notifyError, notifyInformation, notifySuccess} from '../../../shared/notificationUtils';
import {dependentDocument as dependDocumentRedux} from '../redux';
import {actions} from '../redux';
import {
    DELETE_DEPEND_DOCUMENT,
    DOWNLOAD_DEPENDANT_DOCUMENT,
    DOWNLOAD_DOCUMENT,
    DOWNLOAD_DOCUMENT_LINK,
    EDIT_DOCUMENT,
    GET_DETAIL_DOCUMENT_HISTORY,
    LIST_FOLDER_BY_GROUP,
    MASS_UPDATE_DOCUMENT,
    SHARE_DOCUMENT,
    UPLOAD_DEPEND_DOCUMENT,
    GET_DOCUMENT_ID,
    EMPTY_RECYCLE_BIN,

    GET_REASON_ID,
    GET_PARKING_ID,

    VALIDATE_CONTRACT_NUMBER,
    VALIDATE_CONTRACT_NUMBER_FAILED,
    VALIDATE_CONTRACT_NUMBER_SUCCESS,
    VALIDATE_SHIPPING_DOCUMENT,
    VALIDATE_SHIPPING_DOCUMENT_FAILED,
    VALIDATE_SHIPPING_DOCUMENT_SUCCESS,
} from './constants';
import {
    deleteDependDocumentFailed,
    deleteDependDocumentSuccess,
    downloadDependantDocumentFailed,
    downloadDependantDocumentSuccess,
    downloadDocumentFailed,
    downloadDocumentLinkFailed,
    downloadDocumentLinkSuccess,
    downloadDocumentSuccess,
    editDocumentFailed,
    editDocumentSuccess,
    getDetailDocumentHistoryFailed,
    getDetailDocumentHistorySuccess,
    listFoldersByGroupFailed,
    listFoldersByGroupSuccess,
    massUpdateDocumentFailed,
    massUpdateDocumentSuccess,
    shareDocumentFailed,
    shareDocumentSuccess,
    uploadDependDocumentFailed,
    uploadDependDocumentSuccess,
    getDocumentIDFailed,
    getDocumentIDSuccess,
    emptyRecycleBinFailed,
    emptyRecycleBinSuccess,

    getReasonIDFailed,
    getReasonIDSuccess,
    getParkingIDFailed,
    getParkingIDSuccess,

    validateContractNumberFailed,
    validateContractNumberSuccess,
    validateShippingDocumentFailed,
    validateShippingDocumentSuccess
} from './actions';
import pluralize from 'pluralize';
import {convertBase64ToBlog, saveByteArray} from "../../../shared/fileUtils";
import {Endpoint, Constants, Message} from '../constants'

function* listFoldersByGroup({payload: {groupId}}) {
    const url = `${Endpoint.Folder}/folder-by-group`;
    if (!groupId) {
        yield put(listFoldersByGroupSuccess([]));
        return
    }
    const response = yield call(apiClient.get, url, groupId);
    if (response.error) {
        yield put(listFoldersByGroupFailed(response.detail));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(listFoldersByGroupSuccess(response.data));
    }
}

function* shareDocument({payload: {documentIds, userIds, withDependants}}) {
    const response = yield call(apiClient.post, `${Endpoint.Document}/share-document`, {
        document_ids: documentIds,
        user_ids: userIds,
        with_dependants: withDependants,
    });
    if (response.error) {
        yield put(shareDocumentFailed(response.detail));
        const msg = response.detail.response.data.detail;
        yield call(notifyError, msg);
    } else {
        yield call(notifySuccess, 'Successfully shared');
        yield put(shareDocumentSuccess(response.data));
    }
}

function* solveDownloadResponse(response) {
    if (response.error) {
        yield put(downloadDocumentFailed(response.data));
        const msg = response.detail.response.data.detail;
        yield call(notifyError, msg);
    } else {
        yield put(downloadDocumentSuccess(response));

        // Assuming the response contains the pre-signed URL directly
        // const preSignedUrl = response.download_url || response.data?.download_url;
        let preSignedUrl;
        if (response.data && response.data.download_url) {
            preSignedUrl = response.data.download_url;
        } else if (response.download_url) {
            preSignedUrl = response.download_url;
        } else {
            yield call(notifyError, "Download URL is missing.");
            yield put(downloadDocumentFailed({ message: "Download URL missing" }));
            return;
        }

        console.log(preSignedUrl)

        const fileName = preSignedUrl.split('/').pop().split('?')[0];

        // Create an anchor element and set its href to the pre-signed URL
        const link = document.createElement('a');
        link.href = preSignedUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link); // Append to the body to ensure it's in the DOM
        link.click(); // Simulate a click to start the download

        // Clean up by removing the link from the DOM
        document.body.removeChild(link);
    }
}

function* downloadDocument({payload: {documentIds}}) {
    const response = yield call(apiClient.post, `${Endpoint.Document}/download-document`, {
        document_ids: documentIds,
    });

    yield* solveDownloadResponse(response);
}

function* downloadDocumentLink({payload: {query}}) {
    const options = {method: 'get'}
    const response = yield call(apiClient.request, `${Endpoint.Document}/generate-url${query}`, options);
    if (response.error) {
        yield put(downloadDocumentLinkFailed(response.detail));
        const defaultMessage = 'An error has occurred';
        yield call(notifyError, response.detail || defaultMessage);
    } else {
        const link = document.createElement('a');
        link.href = response.data.url;
        const fileName = response.data.fileName;
        link.setAttribute('download', fileName ? fileName : '');
        document.body.appendChild(link);
        link.click();
        yield call(notifyInformation, 'Your file is being downloaded');
        yield delay(1000);
        yield put(downloadDocumentLinkSuccess(''))
    }
}

function* solveDownloadDependentResponse(response) {
    if (response.error) {
        yield put(downloadDependantDocumentFailed(response.data));
        const msg = response.detail.response.data.detail;
        yield call(notifyError, msg);
    } else {
        yield put(downloadDependantDocumentSuccess(response.detail));
        const contentType = response.headers['content-type'];
        const blob = convertBase64ToBlog(response.data, contentType);
        saveByteArray(response.headers['x-file-name'], blob, contentType);
    }
}

function* downloadDependantDocument({payload: {documentIds}}) {
    const response = yield call(apiClient.post, `${Endpoint.Document}/download-dependants`, {
        document_ids: documentIds,
    });

    yield* solveDownloadDependentResponse(response);
}

function* getDetailDocumentHistory({payload: {id}}) {
    const response = yield call(apiClient.list, `${Endpoint.Document}/${id}/history`);
    if (response.error) {
        yield put(getDetailDocumentHistoryFailed(response.detail));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(getDetailDocumentHistorySuccess(response.data));
    }
}

function* editDocument({payload}) {
    const {entity, override} = payload

    const param = {
        endpoint: `${Endpoint.Document}`,
        id: entity.id || entity.resource_id,
        body: entity
    };
    if (override) {
        param.query = '?override=true'
    }

    const response = yield call(apiClient.edit, param);

    if (response.error) {
        if (response.detail && response.detail.response) {
            yield put(editDocumentFailed(
                {
                    error: response.detail.response.status,
                    errorEntity: entity
                })
            );
        } else {
            yield call(notifyError, 'An error has occurred');
        }
    } else {
        yield put(editDocumentSuccess(response.data));
    }
}

function* uploadDependDocument({payload: {id, file, override}}) {
    const url = `${Endpoint.Document}/${id}/dependent-document`;
    let response = yield call(apiClient.post, url, {
        file_name: file.name,
        file_size: file.size,
        override: override,
    });
    if (response.error) {
        if (response.detail && response.detail.response) {
            yield put(uploadDependDocumentFailed(response.detail.response.status));
        } else {
            yield call(notifyError, 'An error has occurred while uploading document to server');
        }
    } else if (response.data && response.data.data) {
        response = yield call(apiClient.uploadFileIntoS3, file, response.data.data.url, response.data.data.fields);

        if (response.status !== 204) {
            const errorMessage = 'An error has occurred while uploading file to s3'
            yield put(uploadDependDocumentFailed(errorMessage));
            yield call(notifySuccess, errorMessage);
        } else {
            yield put(uploadDependDocumentSuccess());
            yield put(dependDocumentRedux.actions.list(undefined, undefined, undefined, undefined, undefined, url))
            yield call(notifySuccess, 'Successfully uploaded');
        }
    } else {
        yield call(notifyError, 'An error has occurred');
    }
}

const getDocumentCoreState = (state) => {
    for (let m in state) {
        if (m === 'DocumentCore') {
            return state[m];
        }
    }
};

function* deleteDependDocument({payload}) {
    const entity = `${Endpoint.Document}`;
    const options = payload.options
    const items = payload.items || payload
    const response = yield call(apiClient.deleteBatch, entity, items, options);

    if (response.error) {
        yield put(deleteDependDocumentFailed(response.detail));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(deleteDependDocumentSuccess(response));
        yield call(notifySuccess, `Successfully deleted ${pluralize('item', items.length, true)}`);

        const state = yield select(getDocumentCoreState);
        if (!!state && state.detail && state.detail.entity) {
            const url = `${Endpoint.Document}/${state.detail.entity.id}/dependent-document`;
            yield put(dependDocumentRedux.actions.list(undefined, undefined, undefined, undefined, undefined, url))
        }
    }
}

function* getDocumentID({payload}) {
    const template = payload
    const response = yield call(apiClient.list, `${Endpoint.Document}/document_id/?template_name=${template}`);
    if (response.error) {
        yield put(getDocumentIDFailed({
            template,
            data: response.detail
        }));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(getDocumentIDSuccess({
            template,
            data: response.data
        }));
    }
}

export function isStringEmpty(string) {
    return !string || string === ''
}

function* massUpdateDocument({payload}) {
    const endpoint = `${Endpoint.Document}/mass-update`
    const {file} = payload;
    const formData = new FormData();
    formData.append('file', file, file.name);
    const response = yield call(apiClient.post, endpoint, formData, {});

    if (response.error) {
        if (response.detail && response.detail.response && response.detail.response.data) {
            if (response.detail.response.status === HttpStatus.BadRequest) {
                yield put(massUpdateDocumentFailed(response.detail));
                yield call(notifyError, response.detail.response.data.detail);
                return
            }
        }
        yield put(massUpdateDocumentFailed(response.detail));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(massUpdateDocumentSuccess(response));
        yield call(
            notifySuccess,
            `Updated ${response.data.success} document(s) successfully and ${response.data.fail} document(s) failed`);
    }
}

function* emptyRecycleBin() {
    const response = yield call(apiClient.post, `${Endpoint.Document}/empty-recycle-bin`);
    if (response.error) {
        yield put(emptyRecycleBinFailed(response.detail));
        const msg = response.detail.response.data.detail;
        yield call(notifyError, msg);
    } else {
        yield call(notifySuccess, 'Successfully empty recycle bin');
        yield put(emptyRecycleBinSuccess(response.data));

        yield put(actions.list());
    }
}

// Unilever Customize Phase 1+ //

function* getReasonID({payload}) {
    const response = yield call(apiClient.list, `${Endpoint.MasterData}/returned-id`);
    if (response.error) {
        yield put(getReasonIDFailed(response.detail));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(getReasonIDSuccess(response.data));
    }
}

function* getParkingID({payload}) {
    const response = yield call(apiClient.list, `${Endpoint.MasterData}/parked-id`);
    if (response.error) {
        yield put(getParkingIDFailed(response.detail));
        yield call(notifyError, 'An error has occurred');
    } else {
        yield put(getParkingIDSuccess(response.data));
    }
}

// Unilever Customize //

function* validateContractNumber({payload}) {
    const state = {
        valid: true,
        message: '',
        loading: false,
        error: null
    }
    if (isStringEmpty(payload)) {
        state.valid = true
        state.message = ''
        yield put(validateContractNumberSuccess(state));
    } else {
        if (Constants.ContractNumber.Regex.test(payload)) {
            const options = {
                method: 'post',
                body: {
                    document_id: payload
                }
            };
            const response = yield call(apiClient.request, `${Endpoint.Document}/check-document-id-existence`, options);
            if (response.error) {
                if (response.detail && response.detail.response) {
                    if (response.detail.response.status === HttpStatus.NotFound) {
                        state.valid = false
                        state.message = Message.ValidaContractNumber.Nonexistent
                        yield put(validateContractNumberSuccess(state));
                    }
                    return
                }
                yield call(validateContractNumberFailed, response.detail);
                yield call(notifyError, 'An error has occurred');
            } else {
                state.valid = true
                state.message = Message.ValidaContractNumber.Valid
                yield put(validateContractNumberSuccess(state));
            }
        } else {
            state.valid = false
            state.message = Message.ValidaContractNumber.Invalid
            yield put(validateContractNumberSuccess(state));
        }
    }
}

function* validateShippingDocument({payload}) {
    const state = {
        valid: true,
        message: '',
        loading: false,
        error: null
    }
    if (isStringEmpty(payload)) {
        state.valid = true
        state.message = ''
        yield put(validateShippingDocumentSuccess(state));
    } else {
        if (Constants.ShippingDocument.Regex.test(payload)) {
            const options = {
                method: 'post',
                body: {
                    document_id: payload
                }
            };
            const response = yield call(apiClient.request, `${Endpoint.Document}/check-document-id-existence`, options);
            if (response.error) {
                if (response.detail && response.detail.response) {
                    if (response.detail.response.status === HttpStatus.NotFound) {
                        state.valid = false
                        state.message = Message.ValidaShippingDocument.Nonexistent
                        yield put(validateShippingDocumentSuccess(state));
                    }
                    return
                }
                yield call(validateShippingDocumentFailed, response.detail);
                yield call(notifyError, 'An error has occurred');
            } else {
                state.valid = true
                state.message = Message.ValidaShippingDocument.Valid
                yield put(validateShippingDocumentSuccess(state));
            }
        } else {
            state.valid = false
            state.message = Message.ValidaShippingDocument.Invalid
            yield put(validateShippingDocumentSuccess(state));
        }
    }
}



export function* watchListFoldersByGroup(): any {
    yield takeEvery(LIST_FOLDER_BY_GROUP, listFoldersByGroup);
}

export function* watchShareDocument(): any {
    yield takeEvery(SHARE_DOCUMENT, shareDocument);
}

export function* watchDownloadDocument(): any {
    yield takeEvery(DOWNLOAD_DOCUMENT, downloadDocument);
}

export function* watchDownloadDocumentLink(): any {
    yield takeEvery(DOWNLOAD_DOCUMENT_LINK, downloadDocumentLink);
}

export function* watchDownloadDependantDocument(): any {
    yield takeEvery(DOWNLOAD_DEPENDANT_DOCUMENT, downloadDependantDocument);
}

export function* watchGetDetailDocumentHistory(): any {
    yield takeEvery(GET_DETAIL_DOCUMENT_HISTORY, getDetailDocumentHistory);
}

export function* watchUploadDependDocument(): any {
    yield takeEvery(UPLOAD_DEPEND_DOCUMENT, uploadDependDocument);
}

export function* watchDeleteDependDocument(): any {
    yield takeEvery(DELETE_DEPEND_DOCUMENT, deleteDependDocument);
}

export function* watchEditDocument(): any {
    yield takeEvery(EDIT_DOCUMENT, editDocument);
}

export function* watchGetDocumentID(): any {
    yield takeEvery(GET_DOCUMENT_ID, getDocumentID);
}

export function* watchMassUpdateDocument(): any {
    yield takeEvery(MASS_UPDATE_DOCUMENT, massUpdateDocument);
}

export function* watchEmptyRecycleBin(): any {
    yield takeEvery(EMPTY_RECYCLE_BIN, emptyRecycleBin);
}

export function* watchGetReasonID(): any {
    yield takeEvery(GET_REASON_ID, getReasonID);
}

export function* watchGetParkingID(): any {
    yield takeEvery(GET_PARKING_ID, getParkingID);
}

export function* watchValidateContractNumber(): any {
    yield takeEvery(VALIDATE_CONTRACT_NUMBER, validateContractNumber);
}

export function* watchValidateShippingDocument(): any {
    yield takeEvery(VALIDATE_SHIPPING_DOCUMENT, validateShippingDocument);
}

function* documentDetailSaga(): any {
    yield all([
        fork(watchListFoldersByGroup),
        fork(watchDownloadDocument),
        fork(watchDownloadDependantDocument),
        fork(watchShareDocument),
        fork(watchGetDetailDocumentHistory),
        fork(watchEditDocument),
        fork(watchUploadDependDocument),
        fork(watchDeleteDependDocument),
        fork(watchGetDocumentID),
        fork(watchMassUpdateDocument),
        fork(watchDownloadDocumentLink),
        fork(watchEmptyRecycleBin),
        fork(watchGetReasonID),
        fork(watchGetParkingID),
        fork(watchValidateContractNumber),
        fork(watchValidateShippingDocument),
    ]);
}

export default documentDetailSaga;
